import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { NAV_LINKS, BRAND } from '../../lib/site-data';

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? 'bg-ink-900/85 backdrop-blur-xl border-b border-white/10' : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="container-editorial flex items-center justify-between h-16 md:h-20">
        <a href="#top" data-testid="nav-logo" className="group flex items-center gap-3">
          <span
            aria-hidden
            className="relative h-10 w-10 rounded-[4px] bg-white shadow-[0_0_0_1px_rgba(255,255,255,0.15)] overflow-hidden shrink-0"
          >
            <img
              src={BRAND.logoUrl}
              alt=""
              className="absolute pointer-events-none select-none"
              style={{
                width: '92px',
                height: '92px',
                left: '-26px',
                top: '-16px',
                maxWidth: 'none',
              }}
            />
          </span>
          <span className="display-heading text-[15px] tracking-tight text-white/95 leading-tight">
            George<span className="text-white/40 mx-1.5">&amp;</span>Milton
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              className="group relative text-[13px] tracking-wide text-white/70 hover:text-white transition-colors duration-300"
            >
              <span>{l.label}</span>
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-signal-500 transition-[width] duration-500 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="#contact"
            data-testid="nav-cta"
            className="hidden md:inline-flex btn-pill btn-outline text-[12px] px-4 py-2"
          >
            Start a Conversation
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            className="md:hidden inline-flex h-10 w-10 items-center justify-center border border-white/15 rounded-full text-white/85 hover:bg-white/5 transition-colors"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="md:hidden overflow-hidden bg-ink-900/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="container-editorial py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between py-2 border-b border-white/5"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  <span className="display-heading text-2xl">{l.label}</span>
                  <ArrowUpRight className="h-4 w-4 text-white/40" />
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
